import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDocumentOverlayOpen } from "./doc-open-state";

// One layer for every floating toast in the app, mounted on <body>.
//
// Why a portal and not just `position: fixed`: fixed only means "against the
// viewport" while no ancestor creates a containing block, and `transform`,
// `filter` and `backdrop-filter` all create one. The partner console renders
// the bell inside the sticky operator header, and that header carries
// `backdrop-filter: blur(14px)` (staff.css, .staff-header-compact). So the
// toast resolved its `bottom: 24px` against a ~64px header band instead of
// the screen and landed on the header, half of it above the top edge
// (Pavel, client detail ?client=..., F51 5.8.). The same trap already bit the
// document overlay, which is why DocOverlay portals too.
//
// Anchoring the toast structurally instead of per surface means no future
// header, drawer or card can capture it again, on any surface, staff or
// client.
const LAYER_ID = "toast-layer";

function ensureLayer(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const existing = document.getElementById(LAYER_ID);
  if (existing) return existing;
  const node = document.createElement("div");
  node.id = LAYER_ID;
  node.className = "toast-layer";
  document.body.appendChild(node);
  return node;
}

export function ToastLayer({ children, keepOverDocument = false }: {
  children: ReactNode;
  // Opt-out for a message that is the DIRECT answer to the reader's own click
  // (today: the read-only notice). Suppressing that one would turn a blocked
  // button into a button that appears to do nothing. Everything that arrives
  // on its own - a notification - stays gated.
  keepOverDocument?: boolean;
}) {
  // Lazy state, so the node is resolved once per mount instead of on every
  // render. Creation is keyed by id, so a second call (StrictMode, a second
  // toast source) reuses the same layer.
  const [layer] = useState(ensureLayer);
  // F97 (Pavel 2026-08-07): a material opened across the whole screen
  // ("Prohlednout a komentovat", the partner's review room) is the client's
  // working surface; a toast lands over the document AND over the comments
  // panel. So the whole layer stands down while one is open.
  //
  // HELD, never dropped: the toast state lives in NotificationCenter and is
  // untouched here, so closing the document re-renders the message on the
  // spot. The bell, the unread count and the mails are not affected at all -
  // this gate decides only whether the message POPS UP now.
  const documentOpen = useDocumentOverlayOpen();
  if (documentOpen && !keepOverDocument) return null;
  // No document (never in the browser build): render in place rather than
  // swallowing the message. The per-toast rules in styles.css still position
  // it, so the fallback degrades to the old behaviour instead of silence.
  if (!layer) return <>{children}</>;
  return createPortal(children, layer);
}
